import { WORKSPACE_GRANTS, type WorkspaceGrant } from './models/workspace';

export interface WorkspacePermissionRule {
  resource: string;
  allow: WorkspaceGrant[];
  deny: WorkspaceGrant[];
}
export interface WorkspaceRole {
  id: string;
  name: string;
  grants: WorkspaceGrant[];
  resourceRules: WorkspacePermissionRule[];
  permissionMode?: 'unified' | 'custom';
}
export type WorkspacePermissionLevel = 'manage' | 'edit' | 'view';
export const WORKSPACE_PERMISSION_LEVELS = [
  { value: 'manage' as const, label: '管理' },
  { value: 'edit' as const, label: '编辑' },
  { value: 'view' as const, label: '查看' },
];
export interface WorkspacePermissionResource {
  key: string;
  title: string;
  kind: 'workspace' | 'page' | 'instance';
  parentKey: string | null;
  icon: string;
}
export interface WorkspaceAccessPolicyView {
  revision: number;
  roles: WorkspaceRole[];
  resources: WorkspacePermissionResource[];
}
export interface WorkspaceResourceScope {
  pageId?: string;
  instanceId?: string;
}
export interface WorkspacePermissionAccess {
  role: string;
  grants: readonly WorkspaceGrant[];
  roleGrants?: readonly WorkspaceGrant[];
  resourceRules?: readonly WorkspacePermissionRule[];
  permissionOverrides?: readonly WorkspacePermissionRule[];
  readOnly?: boolean;
}

export const WORKSPACE_VIEWER_GRANTS: readonly WorkspaceGrant[] = [
  'workspace.read',
  'workspace.layout.read',
  'record.read',
  'realtime.subscribe',
];
export const WORKSPACE_RESOURCE_GRANTS: readonly WorkspaceGrant[] = [
  'workspace.layout.read',
  'workspace.layout.write',
  'page.update',
  'page.archive',
  'component.install',
  'record.read',
  'record.write',
  'record.delete',
  'tool.execute',
  'agent.run',
  'agent.manage',
  'agent.approve',
  'secret.bind',
  'realtime.subscribe',
  'realtime.publish',
];

export function defaultWorkspaceRoles(): WorkspaceRole[] {
  return [
    {
      id: 'manager',
      name: '管理者',
      grants: [...WORKSPACE_GRANTS],
      resourceRules: [],
      permissionMode: 'unified',
    },
    {
      id: 'editor',
      name: '编辑者',
      permissionMode: 'unified',
      resourceRules: [],
      grants: [
        'workspace.read',
        'workspace.layout.read',
        'workspace.layout.write',
        'page.create',
        'page.update',
        'page.archive',
        'component.create',
        'component.edit',
        'component.install',
        'record.read',
        'record.write',
        'record.delete',
        'realtime.subscribe',
        'realtime.publish',
        'tool.execute',
        'agent.run',
      ],
    },
    {
      id: 'viewer',
      name: '查看者',
      grants: [...WORKSPACE_VIEWER_GRANTS],
      resourceRules: [],
      permissionMode: 'unified',
    },
  ];
}

export function workspaceLevelGrants(
  level: WorkspacePermissionLevel,
  resourceOnly = false,
): WorkspaceGrant[] {
  const role = defaultWorkspaceRoles()[{ manage: 0, edit: 1, view: 2 }[level]];
  return role.grants.filter((grant) => !resourceOnly || WORKSPACE_RESOURCE_GRANTS.includes(grant));
}

export function workspaceLevelFromGrants(
  grants: readonly WorkspaceGrant[],
  resourceOnly = false,
): WorkspacePermissionLevel | null {
  const actual = new Set(
    grants.filter((grant) => !resourceOnly || WORKSPACE_RESOURCE_GRANTS.includes(grant)),
  );
  return (
    WORKSPACE_PERMISSION_LEVELS.find(({ value }) => {
      const expected = workspaceLevelGrants(value, resourceOnly);
      return actual.size === expected.length && expected.every((grant) => actual.has(grant));
    })?.value ?? null
  );
}

export function workspaceLevelRule(
  resource: string,
  level: WorkspacePermissionLevel | 'none',
): WorkspacePermissionRule {
  const available = resource === 'workspace' ? WORKSPACE_GRANTS : WORKSPACE_RESOURCE_GRANTS;
  const allow = level === 'none' ? [] : workspaceLevelGrants(level, resource !== 'workspace');
  return { resource, allow, deny: available.filter((grant) => !allow.includes(grant)) };
}

function applyRules(
  grants: Set<WorkspaceGrant>,
  rules: readonly WorkspacePermissionRule[],
  keys: string[],
) {
  for (const key of keys) {
    const rule = rules.find((item) => item.resource === key);
    for (const grant of rule?.allow ?? []) grants.add(grant);
    for (const grant of rule?.deny ?? []) grants.delete(grant);
  }
}

export function workspaceGrantsFor(
  access: WorkspacePermissionAccess,
  scope: WorkspaceResourceScope = {},
): WorkspaceGrant[] {
  if (access.role === 'owner') return [...WORKSPACE_GRANTS];
  const keys = [
    'workspace',
    ...(scope.pageId ? [`page:${scope.pageId}`] : []),
    ...(scope.instanceId ? [`instance:${scope.instanceId}`] : []),
  ];
  const grants = new Set(access.roleGrants ?? access.grants);
  applyRules(grants, access.resourceRules ?? [], keys);
  applyRules(grants, access.permissionOverrides ?? [], keys);
  return WORKSPACE_GRANTS.filter(
    (grant) => grants.has(grant) && (!access.readOnly || WORKSPACE_VIEWER_GRANTS.includes(grant)),
  );
}

export function workspaceCan(
  access: WorkspacePermissionAccess,
  grant: WorkspaceGrant,
  scope: WorkspaceResourceScope = {},
): boolean {
  if (access.role === 'owner') return true;
  const global = workspaceGrantsFor(access);
  if (!global.includes('workspace.read')) return false;
  if (
    scope.instanceId &&
    scope.pageId &&
    !workspaceGrantsFor(access, { pageId: scope.pageId }).includes('workspace.layout.read')
  )
    return false;
  const grants = workspaceGrantsFor(access, scope);
  return (
    grants.includes(grant) &&
    (!(scope.pageId || scope.instanceId) || grants.includes('workspace.layout.read'))
  );
}

export const WORKSPACE_GRANT_LABELS: Record<WorkspaceGrant, string> = {
  'workspace.read': '进入工作台',
  'workspace.configure': '修改工作台设置',
  'workspace.layout.read': '查看页面与组件',
  'workspace.layout.write': '调整布局与组件设置',
  'workspace.member.invite': '邀请协作成员',
  'workspace.member.manage': '管理成员与角色权限',
  'page.create': '新建页面',
  'page.update': '修改页面信息与分组',
  'page.archive': '删除页面',
  'component.create': '创建组件',
  'component.edit': '编辑组件源码',
  'component.publish': '发布组件',
  'component.install': '安装、升级与移除组件',
  'record.read': '读取数据与文件',
  'record.write': '新增和修改数据与文件',
  'record.delete': '删除数据与文件',
  'schedule.manage': '管理定时任务',
  'realtime.subscribe': '接收实时更新',
  'realtime.publish': '发送实时消息',
  'tool.execute': '调用组件工具',
  'agent.run': '使用 Agent',
  'agent.manage': '配置 Agent',
  'agent.approve': '审批 Agent 操作',
  'secret.bind': '绑定凭据',
};
